import {
    defineComponent,
    reactive,
    ref,
    toRaw,
    PropType,
    h,
} from "vue";

import { useLocale }                from "../locales/i18n.js";
import { Filter, FiltersTable }     from "./filters/table.jsx";
import { ExpressionFilter }         from "../logSearch/ExpressionFilter.js";
import {
    serializeFilterValues,
    unserializeFilterValues,
} from "./filters/serialization.js";

export function serializeFilters(values: Map<string, any>) {
    return serializeFilterValues(FiltersTable, values);
}

export async function unserializeFilters(values: any) {
    return unserializeFilterValues(FiltersTable, values);
}

export const Filters = defineComponent({
    props: {
        values: {
            type: Object as PropType<Map<string, any>>,
            default: () => new Map()
        },

        onsave: {
            type: Function as PropType<(values: Map<string, any>) => void>,
            required: true
        },

        mode: {
            type: String as PropType<"structured" | "expression">,
            default: "structured",
        },

        expression: {
            type: String,
            default: "",
        },

        onsaveExpression: {
            type: Function as PropType<(expression: string) => void>,
            required: true
        }
    },

    setup(props) {
        const { i18n }      = useLocale();
        const refSelected   = ref<Filter | null>(null);
        const refMode       = ref(props.mode);
        const values        = props.values
            ? reactive(structuredClone(toRaw(props.values)))
            : reactive(new Map());

        function getValues(filter: Filter) {
            return values.get(filter.name)
                ?? filter.default();
        }

        function onSelect(filter: Filter, filterValues: any) {
            filterValues
                ? values.set(filter.name, filterValues)
                : values.delete(filter.name);

            refSelected.value = null;
        }

        function renderSelection() {
            if (refMode.value === "expression")
                return <ExpressionFilter expression={props.expression} onsave={props.onsaveExpression} />;

            const selected = toRaw(refSelected.value);

            if (selected) {
                return h(selected.component, {
                    filter      : selected,
                    values      : getValues(selected),
                    onselect    : (values: any) => onSelect(selected, values),
                });

            } else {
                return (
                    <>
                        <div class="log-filter-list">
                            {FiltersTable.map((filter) => (
                                <div class="log-filter-item" onClick={() => (refSelected.value = filter)}>
                                    <div class="log-filter-label">
                                        <span class="material-symbols-outlined">{filter.icon}</span>
                                        {filter.label}
                                    </div>

                                    <div class="log-filter-value">
                                        {filter.printSelected(getValues(filter))}
                                    </div>
                                </div>
                            ))}

                            <div class="log-filter-item" onClick={() => values.clear()}>
                                <div class="log-filter-label">
                                    <span class="material-symbols-outlined">filter_alt_off</span>
                                    {i18n("page.logs.filter_clear")}
                                </div>
                            </div>
                        </div>

                        <div class="log-filter-submit" onClick={() => props.onsave(values)}>
                            <span class="material-symbols-outlined">search</span>
                            {i18n("page.logs.search_in_logs")}
                        </div>
                    </>
                );
            }
        }

        return () => (
            <div class="log-filters">
                <div class="log-filter-name">
                    <span class="material-symbols-outlined">filter_alt</span>
                    {i18n("page.logs.filter_logs")}
                </div>

                <div class="log-filter-mode">
                    <button class={{ active: refMode.value === "structured" }} onClick={() => (refMode.value = "structured")}>
                        <span class="material-symbols-outlined">tune</span>
                        Filtres
                    </button>

                    <button class={{ active: refMode.value === "expression" }} onClick={() => (refMode.value = "expression")}>
                        <span class="material-symbols-outlined">data_object</span>
                        Expression
                    </button>
                </div>

                {renderSelection()}
            </div>
        );
    },
});
